import React, { useState } from 'react';
import './QuestionEditor.css';
import RichTextEditor from '../../components/RichTextEditor';

// MCQ Editor Component
const MCQEditor = ({ content, updateContent }) => {
  const getInitialOptions = () => {
    if (content.options && content.options.length > 0) {
      return content.options;
    }
    return [
      { id: '1', text: '', isCorrect: false, feedback: '' },
      { id: '2', text: '', isCorrect: false, feedback: '' },
      { id: '3', text: '', isCorrect: false, feedback: '' },
      { id: '4', text: '', isCorrect: false, feedback: '' }
    ];
  };

  const [options, setOptions] = useState(getInitialOptions);

  const updateOption = (index, field, value) => {
    const newOptions = [...options];
    if (field === 'isCorrect') {
      // Only one correct answer for MCQ
      newOptions.forEach((opt, i) => {
        opt.isCorrect = i === index;
      });
    } else {
      newOptions[index][field] = value;
    }
    setOptions(newOptions);
    updateContent('options', newOptions);
  };

  const addOption = () => {
    const newOptions = [...options, { 
      id: String(options.length + 1), 
      text: '', 
      isCorrect: false, 
      feedback: '' 
    }];
    setOptions(newOptions);
    updateContent('options', newOptions);
  };

  const removeOption = (index) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
      updateContent('options', newOptions);
    }
  };

  return (
    <div className="mcq-editor">
      <div className="form-group">
        <label>Question Stem *</label>
        <RichTextEditor
          value={content.stem || ''}
          onChange={(newStem) => updateContent('stem', newStem)}
          placeholder="Enter your question here..."
          height="150px"
        />
      </div>

      <label>Answer Options *</label>
      {options.map((option, index) => (
        <div key={option.id} className="option-editor">
          <div className="option-header">
            <span>Option {index + 1}</span>
            {options.length > 2 && (
              <button 
                type="button" 
                onClick={() => removeOption(index)}
                className="remove-option-btn"
              >
                Remove
              </button>
            )}
          </div>
          
          <input
            type="text"
            value={option.text}
            onChange={(e) => updateOption(index, 'text', e.target.value)}
            placeholder="Enter option text"
            required
          />
          
          <label className="checkbox-label">
            <input
              type="radio"
              name="correctAnswer"
              checked={option.isCorrect}
              onChange={() => updateOption(index, 'isCorrect', true)}
            />
            Correct Answer
          </label>
          
          <input
            type="text"
            value={option.feedback || ''}
            onChange={(e) => updateOption(index, 'feedback', e.target.value)}
            placeholder="Feedback for this option (optional)"
          />
        </div>
      ))}

      <button type="button" onClick={addOption} className="add-option-btn">
        + Add Option
      </button>

      <div className="form-group">
        <label>General Feedback (Optional)</label>
        <textarea
          value={content.generalFeedback || ''}
          onChange={(e) => updateContent('generalFeedback', e.target.value)}
          placeholder="General feedback shown after answer"
          rows="2"
        />
      </div>
    </div>
  );
};

// Multiple Response Editor
const MultipleResponseEditor = ({ content, updateContent }) => {
  const getInitialOptions = () => {
    if (content.options && content.options.length > 0) {
      return content.options;
    }
    return [
      { id: '1', text: '', isCorrect: false },
      { id: '2', text: '', isCorrect: false },
      { id: '3', text: '', isCorrect: false },
      { id: '4', text: '', isCorrect: false }
    ];
  };

  const [options, setOptions] = useState(getInitialOptions);

  const updateOption = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = value;
    setOptions(newOptions);
    updateContent('options', newOptions);
  };

  const addOption = () => {
    const newOptions = [...options, { 
      id: String(options.length + 1), 
      text: '', 
      isCorrect: false
    }];
    setOptions(newOptions);
    updateContent('options', newOptions);
  };

  const removeOption = (index) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
      updateContent('options', newOptions);
    }
  };

  return (
    <div className="multiple-response-editor">
      <div className="form-group">
        <label>Question Stem *</label>
        <RichTextEditor
          value={content.stem || ''}
          onChange={(newStem) => updateContent('stem', newStem)}
          placeholder="Enter your question (e.g., Select all correct answers)"
          height="150px"
        />
      </div>

      <label>Answer Options * (Check all that are correct)</label>
      {options.map((option, index) => (
        <div key={option.id} className="option-editor">
          <div className="option-header">
            <span>Option {index + 1}</span>
            {options.length > 2 && (
              <button 
                type="button" 
                onClick={() => removeOption(index)}
                className="remove-option-btn"
              >
                Remove
              </button>
            )}
          </div>
          
          <input
            type="text"
            value={option.text}
            onChange={(e) => updateOption(index, 'text', e.target.value)}
            placeholder="Enter option text"
            required
          />
          
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={option.isCorrect}
              onChange={(e) => updateOption(index, 'isCorrect', e.target.checked)}
            />
            Correct Answer
          </label>
        </div>
      ))}

      <button type="button" onClick={addOption} className="add-option-btn">
        + Add Option
      </button>

      <div className="form-group">
        <label>General Feedback (Optional)</label>
        <textarea
          value={content.generalFeedback || ''}
          onChange={(e) => updateContent('generalFeedback', e.target.value)}
          placeholder="Feedback shown after answer"
          rows="2"
        />
      </div>
    </div>
  );
};

// Type-In Editor
const TypeInEditor = ({ content, updateContent }) => {
  const getInitialAnswers = () => {
    if (content.answers && content.answers.length > 0) {
      return content.answers;
    }
    return [{ text: '', alternatives: '' }];
  };

  const [answers, setAnswers] = useState(getInitialAnswers);

  const updateAnswer = (index, field, value) => {
    const newAnswers = [...answers];
    newAnswers[index][field] = value;
    setAnswers(newAnswers);
    updateContent('answers', newAnswers);
  };

  const addAnswer = () => {
    const newAnswers = [...answers, { text: '', alternatives: '' }];
    setAnswers(newAnswers);
    updateContent('answers', newAnswers);
  };

  const removeAnswer = (index) => {
    if (answers.length > 1) {
      const newAnswers = answers.filter((_, i) => i !== index);
      setAnswers(newAnswers);
      updateContent('answers', newAnswers);
    }
  };

  return (
    <div className="type-in-editor">
      <div className="form-group">
        <label>Question Text *</label>
        <RichTextEditor
          value={content.text || ''}
          onChange={(newText) => updateContent('text', newText)}
          placeholder="Use [blank] to indicate where answers go. Example: The capital of France is [Paris]."
          height="150px"
        />
        <small>Tip: Put the correct answer in square brackets [like this]</small>
      </div>

      <label>Acceptable Answers (one per blank)</label>
      {answers.map((answer, index) => (
        <div key={index} className="answer-editor">
          <div className="answer-header">
            <span>Blank {index + 1}</span>
            {answers.length > 1 && (
              <button 
                type="button" 
                onClick={() => removeAnswer(index)}
                className="remove-btn"
              >
                Remove
              </button>
            )}
          </div>
          
          <input
            type="text"
            value={answer.text}
            onChange={(e) => updateAnswer(index, 'text', e.target.value)}
            placeholder="Correct answer"
            required
          />
          
          <input
            type="text"
            value={answer.alternatives || ''}
            onChange={(e) => updateAnswer(index, 'alternatives', e.target.value)}
            placeholder="Alternative correct answers (comma-separated)"
          />
        </div>
      ))}

      <button type="button" onClick={addAnswer} className="add-option-btn">
        + Add Blank
      </button>

      <div className="form-group">
        <label>General Feedback (Optional)</label>
        <textarea
          value={content.generalFeedback || ''}
          onChange={(e) => updateContent('generalFeedback', e.target.value)}
          placeholder="Feedback shown after answer"
          rows="2"
        />
      </div>
    </div>
  );
};

// Order Editor
const OrderEditor = ({ content, updateContent }) => {
  const getInitialItems = () => {
    if (content.items && content.items.length > 0) {
      return content.items;
    }
    return [
      { id: '1', text: '', correctPosition: 1 },
      { id: '2', text: '', correctPosition: 2 },
      { id: '3', text: '', correctPosition: 3 }
    ];
  };

  const [items, setItems] = useState(getInitialItems);

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index].text = value;
    setItems(newItems);
    updateContent('items', newItems);
  };

  const addItem = () => {
    const newItems = [...items, {
      id: String(items.length + 1),
      text: '',
      correctPosition: items.length + 1
    }];
    setItems(newItems);
    updateContent('items', newItems);
  };

  const removeItem = (index) => {
    if (items.length > 2) {
      const newItems = items.filter((_, i) => i !== index);
      // Update correct positions
      const updatedItems = newItems.map((item, idx) => ({
        ...item,
        correctPosition: idx + 1
      }));
      setItems(updatedItems);
      updateContent('items', updatedItems);
    }
  };

  const moveItem = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }

    const newItems = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    
    // Update correct positions
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      correctPosition: idx + 1
    }));
    
    setItems(updatedItems);
    updateContent('items', updatedItems);
  };

  return (
    <div className="order-editor">
      <div className="form-group">
        <label>Instructions *</label>
        <RichTextEditor
          value={content.instructions || ''}
          onChange={(newInstructions) => updateContent('instructions', newInstructions)}
          placeholder="Enter instructions (e.g., 'Put these steps in the correct order')"
          height="120px"
        />
      </div>

      <label>Items to Order * (Arrange in correct order from top to bottom)</label>
      {items.map((item, index) => (
        <div key={item.id} className="order-item-editor">
          <div className="item-number">{index + 1}</div>
          <input
            type="text"
            value={item.text}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder={`Step ${index + 1}`}
            required
          />
          <div className="item-controls">
            <button
              type="button"
              onClick={() => moveItem(index, 'up')}
              disabled={index === 0}
              className="move-btn"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => moveItem(index, 'down')}
              disabled={index === items.length - 1}
              className="move-btn"
            >
              ↓
            </button>
            {items.length > 2 && (
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="remove-btn"
              >
                ×
              </button>
            )}
          </div>
        </div>
      ))}

      <button type="button" onClick={addItem} className="add-option-btn">
        + Add Item
      </button>

      <div className="form-group">
        <label>General Feedback (Optional)</label>
        <textarea
          value={content.generalFeedback || ''}
          onChange={(e) => updateContent('generalFeedback', e.target.value)}
          placeholder="Feedback shown after answer"
          rows="2"
        />
      </div>
    </div>
  );
};

// Matching Editor
const MatchingEditor = ({ content, updateContent }) => {
  const getInitialPairs = () => {
    if (content.pairs && content.pairs.length > 0) {
      return content.pairs;
    }
    return [
      { id: '1', left: '', right: '' },
      { id: '2', left: '', right: '' },
      { id: '3', left: '', right: '' }
    ];
  };

  const [pairs, setPairs] = useState(getInitialPairs);

  const updatePair = (index, side, value) => {
    const newPairs = [...pairs];
    newPairs[index][side] = value;
    setPairs(newPairs);
    updateContent('pairs', newPairs);
  };

  const addPair = () => {
    const newPairs = [...pairs, {
      id: String(pairs.length + 1),
      left: '',
      right: ''
    }];
    setPairs(newPairs);
    updateContent('pairs', newPairs);
  };

  const removePair = (index) => {
    if (pairs.length > 2) {
      const newPairs = pairs.filter((_, i) => i !== index);
      setPairs(newPairs);
      updateContent('pairs', newPairs);
    }
  };

  return (
    <div className="matching-editor">
      <div className="form-group">
        <label>Instructions *</label>
        <RichTextEditor
          value={content.instructions || ''}
          onChange={(newInstructions) => updateContent('instructions', newInstructions)}
          placeholder="Enter instructions (e.g., 'Match the words with their definitions')"
          height="120px"
        />
      </div>

      <label>Matching Pairs *</label>
      {pairs.map((pair, index) => (
        <div key={pair.id} className="matching-pair-editor">
          <div className="pair-number">{index + 1}</div>
          <div className="pair-inputs">
            <input
              type="text"
              value={pair.left}
              onChange={(e) => updatePair(index, 'left', e.target.value)}
              placeholder="Left side (e.g., word)"
              required
            />
            <span className="pair-arrow">↔</span>
            <input
              type="text"
              value={pair.right}
              onChange={(e) => updatePair(index, 'right', e.target.value)}
              placeholder="Right side (e.g., definition)"
              required
            />
          </div>
          {pairs.length > 2 && (
            <button
              type="button"
              onClick={() => removePair(index)}
              className="remove-btn"
            >
              ×
            </button>
          )}
        </div>
      ))}

      <button type="button" onClick={addPair} className="add-option-btn">
        + Add Pair
      </button>

      <div className="form-group">
        <label>General Feedback (Optional)</label>
        <textarea
          value={content.generalFeedback || ''}
          onChange={(e) => updateContent('generalFeedback', e.target.value)}
          placeholder="Feedback shown after answer"
          rows="2"
        />
      </div>
    </div>
  );
};

// Main QuestionEditor Component
const QuestionEditor = ({ question, onSave, onCancel, hideLevelField, defaultLevelId }) => {
  const [formData, setFormData] = useState({
    levelId: question?.levelId || defaultLevelId || '',
    testType: question?.testType || '',
    questionType: question?.questionType || 'mcq',
    lessonCode: question?.lessonCode || '',
    content: question?.content || {}
  });

  const levels = [
    { id: 'level-1', name: 'Level 1 - Roots' },
    { id: 'level-2', name: 'Level 2 - Branches' },
    { id: 'level-3', name: 'Level 3 - Skies' },
    { id: 'level-4', name: 'Level 4 - Rivers' },
    { id: 'level-5', name: 'Level 5 - Mountains' }
  ];

  const testTypes = [
    { id: 'vocab', name: 'Vocabulary' },
    { id: 'grammar', name: 'Grammar' },
    { id: 'comprehension', name: 'Comprehension' }
  ];

  const questionTypes = [
    { id: 'mcq', name: 'Multiple Choice' },
    { id: 'multiple-response', name: 'Multiple Response' },
    { id: 'type-in', name: 'Type In' },
    { id: 'order', name: 'Order' },
    { id: 'matching', name: 'Matching' }
  ];

  // Generate lesson codes based on selected level
  const getLessonCodes = () => {
    if (!formData.levelId) return [];
    
    const levelNum = formData.levelId.split('-')[1]; // Extract "1" from "level-1"
    const lessons = [];
    
    for (let i = 1; i <= 10; i++) {
      lessons.push({
        code: `${levelNum}.${i}`,
        label: `Lesson ${levelNum}.${i}`
      });
    }
    
    return lessons;
  };

  const handleLevelChange = (newLevelId) => {
    setFormData({
      ...formData,
      levelId: newLevelId,
      lessonCode: '' // Reset lesson when level changes
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.levelId || !formData.testType || !formData.questionType) {
      alert('Please fill in all required fields');
      return;
    }

    onSave(formData);
  };

  const updateContent = (field, value) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        [field]: value
      }
    });
  };

  const renderQuestionTypeEditor = () => {
    switch (formData.questionType) {
      case 'mcq':
        return <MCQEditor content={formData.content} updateContent={updateContent} />;
      case 'multiple-response':
        return <MultipleResponseEditor content={formData.content} updateContent={updateContent} />;
      case 'type-in':
        return <TypeInEditor content={formData.content} updateContent={updateContent} />;
      case 'order':
        return <OrderEditor content={formData.content} updateContent={updateContent} />;
      case 'matching':
        return <MatchingEditor content={formData.content} updateContent={updateContent} />;
      default:
        return <div>Please select a question type</div>;
    }
  };

  return (
    <div className="question-editor-overlay">
      <div className="question-editor-modal">
        <div className="modal-header">
          <h2>{question ? 'Edit Question' : 'Add New Question'}</h2>
          <button onClick={onCancel} className="close-button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="question-form">
          <div className="form-section">
            <h3>Question Details</h3>
            
            {!hideLevelField && (
              <div className="form-group">
                <label>Level *</label>
                <select 
                  value={formData.levelId}
                  onChange={(e) => handleLevelChange(e.target.value)}
                  required
                >
                  <option value="">Select Level</option>
                  {levels.map(level => (
                    <option key={level.id} value={level.id}>{level.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Test Type *</label>
              <select 
                value={formData.testType}
                onChange={(e) => setFormData({...formData, testType: e.target.value})}
                required
              >
                <option value="">Select Test Type</option>
                {testTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Question Type *</label>
              <select 
                value={formData.questionType}
                onChange={(e) => {
                  setFormData({
                    ...formData, 
                    questionType: e.target.value,
                    content: {}
                  });
                }}
                required
              >
                {questionTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            {!hideLevelField && (
              <div className="form-group">
                <label>Lesson (Optional)</label>
                <select 
                  value={formData.lessonCode}
                  onChange={(e) => setFormData({...formData, lessonCode: e.target.value})}
                  disabled={!formData.levelId}
                >
                  <option value="">Select Lesson (Optional)</option>
                  {getLessonCodes().map(lesson => (
                    <option key={lesson.code} value={lesson.code}>
                      {lesson.label}
                    </option>
                  ))}
                </select>
                {!formData.levelId && (
                  <small style={{color: '#999'}}>Select a level first to choose a lesson</small>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Question Content</h3>
            {renderQuestionTypeEditor()}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn">
              {question ? 'Update Question' : 'Add Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionEditor;